"use client";

import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MDEditor from '@uiw/react-md-editor';
import { getPrivacyPolicy, savePrivacyPolicy, PrivacyPolicy } from '@/services/policyService';
import { getUserInfo } from '@/services/userService';
import { useTheme } from '@/contexts/ThemeContext';
import { getAuthUID } from '@/lib/authState';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, PenTool, Eye, FileUp } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { useOnboarding } from "@/onboarding/OnboardingContext";

// Privacy Policy template
const PRIVACY_POLICY_TEMPLATE = `**[Company Name] Privacy Policy**

Last updated: [insert date]

**INTRODUCTION**

Welcome to [insert full legal name of entity] (ACN xxx)(**"We", "us" and "our"**). This Privacy Policy explains how and why we collect, use, hold, disclose, and protect your Personal Information.

By accessing or using our services, products, or website you consent to the practices described in this Privacy Policy. Please read this policy carefully to understand our privacy practices.

**WHAT IS PERSONAL INFORMATION?**

Personal Information is any information or an opinion about an identified individual or an individual who can be reasonably identified from the information or opinion. Information or an opinion may be Personal Information regardless of whether it is true.

**WHAT PERSONAL INFORMATION DO WE COLLECT AND HOLD?**

We collect Personal Information from you and about you when you interact with our website, marketing campaigns, products, services and/or when you contact us (e.g call us, send us an email, or interact with us via social media).

The Personal Information we collect may include, but is not limited to:

- Name
- Contact information (e.g. email address, phone number)
- Date of birth
- Postal address
- Payment information
- Other information you provide to us voluntarily

We collect Personal Information through various means, including but not limited to the following:

- When you register an account with us
- When you place an order of make a purchase
- When you engage our services
- When you participate in surveys, contests or promotions
- When you contact our customer support
- When you interact with our online ads on Google, Bing or social media

If you interact with us via a social media platform you agree to allow us to receive information about you from the social media platform, which may include your Personal Information (if you directly communicate with us via the social media platform), information about your visits and interaction with our website.

We [do not] generally collect sensitive information and we will only collect sensitive information about you with your consent (unless we are otherwise required or authorised by or under law to do so).

**COOKIES AND SIMILAR TECHNOLOGY**

We may use cookies and similar technology on our website to enhance your user experience. Cookies are small text files that are stored on your device when you visit our website. You can control the use of cookies through your browser settings.

We may also collect information about how you access, use, and interact with our website. We also use a range of other tools such as Google Analytics [and [LIST ANY OTHER TOOLS]] to collect information about how you use, access and interact with website. This information may include:

**(a)** the location from which you have come to the site and the pages you have visited; and

**(b)** technical data, which may include IP address, the types of devices you are using to access the website, device attributes, browser type, language, and operating system; and

**WHY DO WE COLLECT AND USE YOUR PERSONAL INFORMATION?**

We collect, hold, and use your Personal Information so that we can:

**(a)** provide and improve our products and services;

**(b)** manage our relationship with you;

**(c)** to communicate with you, respond to your enquires or complaints and provide customer support;

**(d)** to comply with our legal obligations and resolve disputes;

**(e)** identify and tell you about other products or services that we think may be of interest to you; and

**(f)** to consider job applicants for current and future employment.

If you do not provide us with your Personal Information, we may not be able to provide you with our products, communicate with you or respond to your enquiries.

**SECURITY OF PERSONAL INFORMATION**

We take reasonable steps to protect your Personal Information from unauthorised access, interference, disclosure, or destruction. We use secure encryption and follow industry best practices to safeguard your Personal Information.

We store most information about you in computer systems and databases (including cloud service providers) operated by either us or our external service providers. Some information about you is recorded in paper files that we store securely.

We will also take reasonable steps to destroy or de-identify Personal Information once no longer required, unless we are legally required to retain it.

While we strive to protect your Personal Information, we cannot guarantee its absolute security.

**WHO DO WE DISCLOSE YOUR PERSONAL INFORMATION TO, AND WHY?**

We may transfer or disclose your Personal Information to our related companies.

- We may disclose Personal Information to external service providers so that they may perform services for us or on our behalf, which includes [name a main business you outsource work to. For example, you might share Personal Information to a delivery partner such as Australia Post who will deliver products on behalf of your business].

We may also disclose your Personal Information to others where:

**(a)** we are required or authorised by law to do so;

**(b)** you have expressly consented to the disclosure, or consent may be reasonably inferred from the circumstances; or

**(c)** we need to share it with data processors to operate our business and provide products to you.

If the ownership or control of all or part of our business changes, we may transfer your Personal Information to the new owner.

**DISCLOSE TO OVERSEAS RECIPIENTS**

We do not disclose your Personal Information to any recipients outside Australia. **OR**

We may disclose your Personal Information to recipients outside of Australia who are in the following countries [insert countries].

**ACCESS TO AND CORRECTION OF YOUR PERSONAL INFORMATION**

You may request access to or correction of Personal Information that we hold about you by contacting us directly. Our contact details are set out below. Please understand there are some circumstances in which we are not required to give you access to your Personal Information, but we will advise you if these circumstances apply to your request.

There is no charge for requesting access to your Personal Information, but we may require you to meet our reasonable costs in providing you with access (such as photocopying costs or costs for time spent on collating large amounts of material).

We will respond to your requests to access or correct Personal Information in a reasonable time and will take all reasonable steps to ensure that the Personal Information we hold about you remains accurate, up-to-date and correct.

**COMPLAINTS**

If you have a complaint about the way in which we have handled any privacy issue, including your request for access or correction of your Personal Information, you should contact us. Our contact details are set out below.

We will consider your complaint and determine whether it requires further investigation. We will notify you of the outcome of this investigation and any subsequent internal investigation.

If you remain unsatisfied with the way in which we have handled a privacy issue, you may make a complaint to the Office of the Australian Information Commissioner (OAIC) (www.oaic.gov.au).

**CONTACT DETAILS**

If you have any questions, comments, requests, complaints, or concerns, please contact us at:

Privacy Officer

[Contact address]

[Email address for privacy officer]

[Phone number]

**CHANGES TO THIS POLICY**

We may update this Privacy Policy from time to time. The updated version will be posted on our website with the effective date. Please check this page periodically for any changes.
`;

export default function ModalPrivacyPolicyImportExample() {
  const [open, setOpen] = useState(true);
  const [policy, setPolicy] = useState<PrivacyPolicy | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('editor');
  const { setStatus, openStep, closeActive, goBack } = useOnboarding();
  const [userInfo, setUserInfo] = useState<{ email?: string; firstName?: string; lastName?: string; phoneNumber?: string } | null>(null);

  useEffect(() => {
    if (open) {
        fetchPolicy();
        fetchUserInfo();
    }
  }, [open]);

  const fetchUserInfo = async () => {
    try {
      const info = await getUserInfo();
      if (info) {
        setUserInfo(info);
      }
    } catch (error) {
      console.error("Failed to fetch user info", error);
    }
  };

  const fetchPolicy = async () => {
    setLoading(true);
    try {
      const companyID = window.companyID;
      if (!companyID) {
          console.warn("No companyID found");
          return;
      }
      const data = await getPrivacyPolicy(companyID);
      setPolicy(data);
      const content = data?.content ? decodeURIComponent(data.content) : '';
      setDraft(content);
    } catch (error) {
      console.error("Failed to fetch policy", error);
      toast.error("Failed to fetch privacy policy");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const companyID = window.companyID;
      const uid = getAuthUID();
      if (!companyID || !uid) throw new Error("Missing company ID or User ID");

      await savePrivacyPolicy(
        companyID,
        draft,
        uid,
        policy?.version || '0.0',
        policy?.content ? decodeURIComponent(policy.content) : ''
      );

      toast.success("Privacy Policy saved successfully");
      setStatus("import_privacy_policy", "done");
      openStep("import_collection_notice");
      await fetchPolicy();
    } catch (error) {
      console.error("Failed to save policy", error);
      toast.error("Failed to save privacy policy");
    } finally {
      setSaving(false);
    }
  };

  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    setUploading(true);

    try {
      // Handle DOCX files
      if (fileName.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const mammoth = await import('mammoth');
        
        // Convert to HTML first, then to simple markdown-like text
        const result = await mammoth.convertToHtml({ arrayBuffer });
        
        // Convert HTML to markdown-ish format
        let markdown = result.value
          .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n')
          .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n')
          .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n')
          .replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n')
          .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
          .replace(/<b>(.*?)<\/b>/gi, '**$1**')
          .replace(/<em>(.*?)<\/em>/gi, '*$1*')
          .replace(/<i>(.*?)<\/i>/gi, '*$1*')
          .replace(/<li>(.*?)<\/li>/gi, '- $1\n')
          .replace(/<\/ul>/gi, '\n')
          .replace(/<\/ol>/gi, '\n')
          .replace(/<ul[^>]*>/gi, '')
          .replace(/<ol[^>]*>/gi, '')
          .replace(/<p>(.*?)<\/p>/gi, '$1\n\n')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<[^>]+>/g, '') // Remove remaining HTML tags
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/\n{3,}/g, '\n\n') // Normalize multiple newlines
          .trim();
        
        setDraft(markdown);
        setActiveTab('editor');
        toast.success('Word document converted and loaded');
      }
      // Handle PDF files
      else if (fileName.endsWith('.pdf')) {
        const arrayBuffer = await file.arrayBuffer();
        const pdfjs = await import('pdfjs-dist');
        
        // Configure worker
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items
            .map((item: any) => item.str)
            .join(' ');
          fullText += pageText + '\n\n';
        }
        
        setDraft(fullText.trim());
        setActiveTab('editor');
        toast.success('PDF text extracted and loaded');
      }
      // Handle text/markdown files
      else if (fileName.endsWith('.txt') || fileName.endsWith('.md')) {
        const text = await file.text();
        setDraft(text);
        setActiveTab('editor');
        toast.success('File content loaded into editor');
      }
      else {
        toast.error('Unsupported file type. Please use .docx, .pdf, .txt, or .md files.');
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to process file. Please try again or copy/paste the content manually.');
    } finally {
      setUploading(false);
      // Reset the input so the same file can be selected again
      e.target.value = '';
    }
  };

  /**
   * Replace template placeholders with actual company data
   */
  const fillTemplateVariables = (template: string): string => {
    const companyName = window.companyName || '[Company Name]';
    // Use fetched user info for privacy officer details
    const ownerEmail = userInfo?.email || '[Email address for privacy officer]';
    const ownerName = userInfo?.firstName && userInfo?.lastName 
      ? `${userInfo.firstName} ${userInfo.lastName}` 
      : userInfo?.firstName || 'Privacy Officer';
    const ownerPhone = userInfo?.phoneNumber || '[Phone number]';
    const today = new Date().toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    
    return template
      // Company name variations
      .replace(/\[Company Name\]/g, companyName)
      .replace(/\[insert full legal name of entity\]/g, companyName)
      .replace(/\[name of entity\]/g, companyName)
      // Date
      .replace(/\[insert date\]/g, today)
      // Privacy Officer / Contact details (tenancy owner by default)
      .replace(/^Privacy Officer$/m, ownerName)
      .replace(/\[Contact address\]/g, '[Contact address]')
      .replace(/\[Email address for privacy officer\]/g, ownerEmail)
      .replace(/\[Phone number\]/g, ownerPhone);
  };

  const applyTemplate = (templateContent: string) => {
      const filledTemplate = fillTemplateVariables(templateContent);
      setDraft(filledTemplate);
      setActiveTab('editor');
      toast.success("Template applied with your company details");
  };

  const templates = [
      { name: "Standard Privacy Policy", content: PRIVACY_POLICY_TEMPLATE },
  ];

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Privacy Policy Editor</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) closeActive(); }}>
        <DialogContent className="w-full! max-w-[95vw]! xl:max-w-[85vw]! 2xl:max-w-[1600px]! h-[95vh]! md:h-[90vh]! bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full h-full overflow-hidden shadow-2xl border border-white/10 flex flex-col">
            {/* Close button */}
            <button
              onClick={() => { setOpen(false); closeActive(); }}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Header with tabs and actions */}
            <div className="shrink-0 p-4 md:p-6 pb-0 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">Add your Privacy Policy</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Select from one of the options below to add your Privacy Policy to de.iterate</p>
                </div>
                <div className="flex gap-2 shrink-0 mr-10">
                  <Button variant="outline" size="sm" onClick={() => { setOpen(false); goBack(); }}>Back</Button>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save & Continue
                  </Button>
                </div>
              </div>
              
              {!loading && (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="bg-transparent p-0 gap-1 md:gap-4 flex-wrap">
                    <TabsTrigger value="editor" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <PenTool className="w-4 h-4 mr-1.5" /> Editor
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <Upload className="w-4 h-4 mr-1.5" /> Upload
                    </TabsTrigger>
                    <TabsTrigger value="templates" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <FileText className="w-4 h-4 mr-1.5" /> Templates
                    </TabsTrigger>
                    <TabsTrigger value="preview" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary border border-transparent data-[state=active]:border-primary/20 rounded-md px-3 py-1.5 text-sm">
                      <Eye className="w-4 h-4 mr-1.5" /> Preview
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden">
                {loading ? (
                    <div className="h-full flex justify-center items-center">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ) : (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-black/5">
                            <TabsContent value="editor" className="h-full mt-0 m-0 w-full">
                                <MDEditor
                                    value={draft}
                                    onChange={v => setDraft(v || '')}
                                    data-color-mode={isDark ? 'dark' : 'light'}
                                    className="h-full w-full shadow-none border border-white/10 rounded-md overflow-hidden"
                                    preview="edit"
                                    hideToolbar={false}
                                    style={{ minHeight: 'calc(100vh - 280px)', width: '100%' }}
                                />
                            </TabsContent>

                            <TabsContent value="upload" className="h-full mt-0 m-0 flex flex-col items-center justify-center">
                                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 md:p-12 text-center bg-white/5 backdrop-blur-sm max-w-2xl w-full">
                                    {uploading ? (
                                      <>
                                        <Loader2 className="w-12 h-12 mx-auto text-primary mb-4 animate-spin" />
                                        <h3 className="text-lg font-medium mb-2">Processing Document...</h3>
                                        <p className="text-sm text-muted-foreground">Converting your document to editable format</p>
                                      </>
                                    ) : (
                                      <>
                                        <FileUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                        <h3 className="text-lg font-medium mb-2">Upload Document</h3>
                                        <p className="text-sm text-muted-foreground mb-2">Supports Word (.docx), PDF, Markdown, and Text files.</p>
                                        <p className="text-xs text-muted-foreground/70 mb-6">Your document will be converted to editable text format.</p>
                                        <input
                                            id="policy-upload"
                                            type="file"
                                            className="hidden"
                                            accept=".docx,.pdf,.md,.txt"
                                            onChange={handleFileUpload}
                                        />
                                        <Button asChild variant="outline">
                                            <label htmlFor="policy-upload" className="cursor-pointer">
                                                Select File
                                            </label>
                                        </Button>
                                      </>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="templates" className="h-full mt-0 m-0">
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {templates.map((t, i) => (
                                        <div key={i} className="p-4 border border-white/10 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer" onClick={() => applyTemplate(t.content)}>
                                            <h3 className="font-medium mb-2">{t.name}</h3>
                                            <p className="text-xs text-muted-foreground">Use our generic template to get you started, making sure you have your legal team review and approve this policy is correct and inline with your businesses obligations under the Privacy Act.</p>
                                            <Button size="sm" variant="secondary" className="mt-4 w-full">Use Template</Button>
                                        </div>
                                    ))}
                                </div>
                            </TabsContent>

                            <TabsContent value="preview" className="h-full mt-0 m-0 overflow-y-auto">
                                <div className="prose dark:prose-invert max-w-none p-6 md:p-8 bg-card rounded-md border border-white/10">
                                    <ReactMarkdown remarkPlugins={[[remarkGfm, { breaks: true }]]}>
                                        {draft || '*No content to preview.*'}
                                    </ReactMarkdown>
                                </div>
                            </TabsContent>
                        </div>
                    </Tabs>
                )}
            </div>

          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
